// src/pages/agro/Animales.js
import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'

const ESTADO_BADGE = {
  activo: 'badge-green',
  vendido: 'badge-amber',
  muerto: 'badge-red',
  transferido: 'badge-blue',
}

const EMPTY_FORM = {
  codigo: '', nombre: '', especie_id: '', raza_id: '',
  sexo: 'macho', fecha_nacimiento: '', peso_kg: '',
  color: '', estado: 'activo', notas: '',
}

export default function Animales() {
  const { profile } = useAuth()
  const canWrite = ['admin', 'capataz', 'veterinario'].includes(profile?.rol)

  const [animales, setAnimales] = useState([])
  const [especies, setEspecies] = useState([])
  const [razas, setRazas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [filtroEspecie, setFiltroEspecie] = useState('')

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    const [animRes, espRes] = await Promise.all([
      supabase.from('animales').select(`
        *, especie:especies(nombre), raza:razas(nombre)
      `).order('created_at', { ascending: false }),
      supabase.from('especies').select('*').order('nombre'),
    ])
    setAnimales(animRes.data ?? [])
    setEspecies(espRes.data ?? [])
    setLoading(false)
  }

  async function loadRazas(especieId) {
    if (!especieId) { setRazas([]); return }
    const { data } = await supabase.from('razas').select('*').eq('especie_id', especieId).order('nombre')
    setRazas(data ?? [])
  }

  function openNew() {
    setForm(EMPTY_FORM); setEditId(null); setRazas([]); setModal(true)
  }
  function openEdit(a) {
    setForm({
      codigo: a.codigo, nombre: a.nombre ?? '', especie_id: a.especie_id ?? '',
      raza_id: a.raza_id ?? '', sexo: a.sexo, fecha_nacimiento: a.fecha_nacimiento ?? '',
      peso_kg: a.peso_kg ?? '', color: a.color ?? '', estado: a.estado, notas: a.notas ?? '',
    })
    setEditId(a.id)
    if (a.especie_id) loadRazas(a.especie_id)
    setModal(true)
  }

  function onChange(e) {
    const { name, value } = e.target
    setForm(f => ({ ...f, [name]: value }))
    if (name === 'especie_id') { loadRazas(value); setForm(f => ({ ...f, raza_id: '', especie_id: value })) }
  }

  async function save() {
    if (!form.codigo.trim()) return alert('El código es obligatorio')
    setSaving(true)
    const payload = { ...form, created_by: profile.id, peso_kg: form.peso_kg || null }
    if (editId) {
      await supabase.from('animales').update(payload).eq('id', editId)
    } else {
      await supabase.from('animales').insert([payload])
    }
    setSaving(false); setModal(false); loadData()
  }

  async function deleteAnimal(id) {
    if (!window.confirm('¿Eliminar este animal?')) return
    await supabase.from('animales').delete().eq('id', id)
    loadData()
  }

  const filtered = animales.filter(a => {
    const matchSearch = !search || a.codigo.toLowerCase().includes(search.toLowerCase()) || (a.nombre ?? '').toLowerCase().includes(search.toLowerCase())
    const matchEspecie = !filtroEspecie || a.especie_id === filtroEspecie
    return matchSearch && matchEspecie
  })

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🐄 Animales</div>
          <div className="page-subtitle">Registro de ganado activo e histórico</div>
        </div>
        {canWrite && <button className="btn btn-primary" onClick={openNew}>+ Nuevo animal</button>}
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <input className="form-input" style={{ maxWidth: 260 }} placeholder="Buscar por código o nombre..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="form-select" style={{ maxWidth: 200 }} value={filtroEspecie} onChange={e => setFiltroEspecie(e.target.value)}>
            <option value="">Todas las especies</option>
            {especies.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
          <div className="text-muted text-sm" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            {filtered.length} animales
          </div>
        </div>

        <div className="table-wrap">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-dark" style={{ margin: 'auto' }} /></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Especie</th>
                  <th>Raza</th>
                  <th>Sexo</th>
                  <th>Nac.</th>
                  <th>Peso</th>
                  <th>Estado</th>
                  {canWrite && <th>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="empty-state">Sin registros</td></tr>
                )}
                {filtered.map(a => (
                  <tr key={a.id}>
                    <td><span className="mono">{a.codigo}</span></td>
                    <td>{a.nombre ?? '—'}</td>
                    <td>{a.especie?.nombre ?? '—'}</td>
                    <td>{a.raza?.nombre ?? '—'}</td>
                    <td>{a.sexo === 'macho' ? '♂' : '♀'} {a.sexo}</td>
                    <td>{a.fecha_nacimiento ? new Date(a.fecha_nacimiento).toLocaleDateString('es') : '—'}</td>
                    <td>{a.peso_kg ? `${a.peso_kg} kg` : '—'}</td>
                    <td><span className={`badge ${ESTADO_BADGE[a.estado]}`}>{a.estado}</span></td>
                    {canWrite && (
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(a)}>✏️</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => deleteAnimal(a.id)}>🗑️</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="modal-title">{editId ? 'Editar animal' : 'Nuevo animal'}</div>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}>✕</button>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Código / Arete *</label>
                <input className="form-input" name="codigo" value={form.codigo} onChange={onChange} placeholder="Ej: BOV-001" />
              </div>
              <div className="form-group">
                <label className="form-label">Nombre</label>
                <input className="form-input" name="nombre" value={form.nombre} onChange={onChange} placeholder="Opcional" />
              </div>
              <div className="form-group">
                <label className="form-label">Especie</label>
                <select className="form-select" name="especie_id" value={form.especie_id} onChange={onChange}>
                  <option value="">Seleccionar...</option>
                  {especies.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Raza</label>
                <select className="form-select" name="raza_id" value={form.raza_id} onChange={onChange}>
                  <option value="">Seleccionar...</option>
                  {razas.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Sexo</label>
                <select className="form-select" name="sexo" value={form.sexo} onChange={onChange}>
                  <option value="macho">Macho</option>
                  <option value="hembra">Hembra</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fecha de nacimiento</label>
                <input className="form-input" type="date" name="fecha_nacimiento" value={form.fecha_nacimiento} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Peso (kg)</label>
                <input className="form-input" type="number" step="0.1" name="peso_kg" value={form.peso_kg} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Color / Marcas</label>
                <input className="form-input" name="color" value={form.color} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" name="estado" value={form.estado} onChange={onChange}>
                  <option value="activo">Activo</option>
                  <option value="vendido">Vendido</option>
                  <option value="muerto">Muerto</option>
                  <option value="transferido">Transferido</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notas</label>
              <textarea className="form-textarea" name="notas" value={form.notas} onChange={onChange} />
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
